import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  getVoiceGuidancePrefs,
  isSpeechSynthesisSupported,
  setVoiceGuidancePrefs,
  speakText,
  stopSpeaking,
} from "../utils/voiceGuidance";

const VoiceGuidanceContext = createContext(null);

export function VoiceGuidanceProvider({ children }) {
  const [prefs, setPrefsState] = useState(() => getVoiceGuidancePrefs());
  const [speaking, setSpeaking] = useState(false);
  const supported = isSpeechSynthesisSupported();

  useEffect(() => {
    function onPrefs(event) {
      if (event?.detail) {
        setPrefsState(event.detail);
        return;
      }
      setPrefsState(getVoiceGuidancePrefs());
    }
    window.addEventListener("nema:voice-guidance-prefs", onPrefs);
    return () => window.removeEventListener("nema:voice-guidance-prefs", onPrefs);
  }, []);

  useEffect(() => () => stopSpeaking(), []);

  const setEnabled = useCallback((enabled) => {
    const next = setVoiceGuidancePrefs({ enabled: Boolean(enabled) });
    setPrefsState(next);
    if (!enabled) stopSpeaking();
  }, []);

  const setAutoSpeak = useCallback((autoSpeak) => {
    const next = setVoiceGuidancePrefs({ autoSpeak: Boolean(autoSpeak) });
    setPrefsState(next);
  }, []);

  const stop = useCallback(() => {
    stopSpeaking();
    setSpeaking(false);
  }, []);

  const speak = useCallback(
    (text, utteranceKey = "") => {
      if (!supported || !String(text || "").trim()) return;
      stopSpeaking();
      setSpeaking(true);
      speakText(text, {
        onStart: () => setSpeaking(true),
        onEnd: () => setSpeaking(false),
        onError: () => setSpeaking(false),
      });
      if (utteranceKey) {
        /* key used by callers for auto-speak dedupe only */
      }
    },
    [supported]
  );

  const value = useMemo(
    () => ({
      supported,
      prefs,
      speaking,
      setEnabled,
      setAutoSpeak,
      speak,
      stop,
    }),
    [supported, prefs, speaking, setEnabled, setAutoSpeak, speak, stop]
  );

  return <VoiceGuidanceContext.Provider value={value}>{children}</VoiceGuidanceContext.Provider>;
}

export function useVoiceGuidanceContext() {
  const ctx = useContext(VoiceGuidanceContext);
  if (!ctx) {
    return {
      supported: isSpeechSynthesisSupported(),
      prefs: getVoiceGuidancePrefs(),
      speaking: false,
      setEnabled: () => {},
      setAutoSpeak: () => {},
      speak: () => {},
      stop: stopSpeaking,
    };
  }
  return ctx;
}
